import React, { useId } from "react";
import { ColorblindImage } from "./ColorblindImage";
import { verifyImage } from "../utils/imageVerification";
import { canPairUploadedImage, readFileAsDataUrl } from "../utils/slideImages";

interface PairSlideImagesProps {
  primarySrc: string;
  secondarySrc?: string;
  alt: string;
  imageClassName?: string;
  onSecondaryChange: (src: string | undefined) => void;
}

const PairSlideImages: React.FC<PairSlideImagesProps> = ({
  primarySrc,
  secondarySrc,
  alt,
  imageClassName = "max-h-40 w-full object-contain",
  onSecondaryChange,
}) => {
  const inputId = useId();
  const allowSecondImage = canPairUploadedImage(primarySrc);

  const handleFile = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    event.target.value = "";
    if (!file) {
      return;
    }

    const result = await verifyImage(file);
    if (!result.isValid) {
      alert(result.message);
      return;
    }

    try {
      const dataUrl = await readFileAsDataUrl(file);
      onSecondaryChange(dataUrl);
    } catch (error) {
      console.error("Error reading second image:", error);
      alert("Could not read that image. Please try another file.");
    }
  };

  return (
    <div className="w-full">
      <div className={secondarySrc ? "grid grid-cols-2 items-center gap-2" : ""}>
        <ColorblindImage src={primarySrc} alt={alt} className={imageClassName} />
        {secondarySrc ? (
          <ColorblindImage
            src={secondarySrc}
            alt={`${alt} 2`}
            className={imageClassName}
          />
        ) : null}
      </div>
      {allowSecondImage ? (
        <div
          className="mt-2 flex flex-col items-center gap-1"
          onPointerDown={(event) => event.stopPropagation()}
        >
          {secondarySrc ? (
            <>
              <p className="text-center text-xs text-gray-600">
                These two pictures show side by side on one slide.
              </p>
              <button
                type="button"
                onClick={() => onSecondaryChange(undefined)}
                className="text-sm font-bold text-red-600 underline"
              >
                Remove second image
              </button>
            </>
          ) : (
            <>
              <label
                htmlFor={inputId}
                className="cursor-pointer text-center text-sm font-bold text-blue-600 underline"
              >
                Add a second image beside this
              </label>
              <input
                id={inputId}
                type="file"
                accept="image/jpeg,image/png,image/gif,image/webp"
                className="hidden"
                onChange={handleFile}
              />
              <p className="text-center text-xs text-gray-600">
                Optional. Both pictures stay on the same full-screen slide.
              </p>
            </>
          )}
        </div>
      ) : null}
    </div>
  );
};

export default PairSlideImages;
